// frontend/src/pages/ContactSupport.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Mail,
  User,
  Tag,
  Send,
  CheckCircle2,
  Clock,
  LifeBuoy,
  ShieldCheck,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import API from "../api/api";
import "../style/contact-support.css";

const SUPPORT_EMAIL = "grgptsupport@gmail.com";

const QUICK_TOPICS = [
  "Account & Credits",
  "Subscription & Billing",
  "GR Search & Documents",
  "Bug / Technical Issue",
  "General Inquiry",
];

export default function ContactSupport() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.user);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Auto-populate user details if authenticated
  useEffect(() => {
    window.scrollTo(0, 0);
    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || currentUser.name || "",
        email: prev.email || currentUser.email || "",
      }));
    }
  }, [currentUser]);

  const handleBack = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (
      window.history.state &&
      typeof window.history.state.idx === "number" &&
      window.history.state.idx > 0
    ) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
    if (serverError) {
      setServerError("");
    }
  };

  const handleSelectTopic = (topic) => {
    handleInputChange("subject", topic);
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name.trim()) {
      newErrors.name = "Please enter your name";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email address";
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Please enter a subject";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Please enter your message";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Please provide a little more detail (at least 10 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || loading) return;

    setLoading(true);
    setServerError("");

    try {
      const response = await API.post("/contact", {
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      });

      if (response?.data?.success) {
        setIsSubmitted(true);
      } else {
        setServerError(
          response?.data?.message ||
            "Unable to send message right now. Please email grgptsupport@gmail.com directly."
        );
      }
    } catch (err) {
      console.error("Support form submission error:", err);
      const errorMsg =
        err.response?.data?.message ||
        "Failed to send support email. Please reach out to grgptsupport@gmail.com directly.";
      setServerError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetForm = () => {
    setIsSubmitted(false);
    setFormData({
      name: currentUser?.name || "",
      email: currentUser?.email || "",
      subject: "",
      message: "",
    });
    setErrors({});
    setServerError("");
  };

  return (
    <div className="contact-page-container">
      {/* Top back button */}
      <div className="contact-top-nav">
        <button
          type="button"
          className="contact-back-btn"
          onClick={handleBack}
          aria-label="Back"
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>
      </div>

      {/* Header section */}
      <div className="contact-header">
        <div className="contact-badge">
          <LifeBuoy size={14} />
          <span>Help & Support</span>
        </div>
        <h1>Contact Support</h1>
        <p className="contact-subtitle">
          Have an inquiry, feedback, or need assistance? Fill out the form below
          to email our support team directly at <strong>{SUPPORT_EMAIL}</strong>.
        </p>
      </div>

      <div className="contact-grid">
        {/* Left Side: Direct Contact Details & Info */}
        <div className="contact-info-panel">
          <div className="contact-info-card">
            <div className="info-card-header">
              <div className="info-icon-badge">
                <HelpCircle size={20} />
              </div>
              <div>
                <h3 className="info-title">Need Help?</h3>
                <p className="info-card-desc">Tips for faster resolution</p>
              </div>
            </div>

            <ul className="info-checklist">
              <li>
                <ShieldCheck size={16} className="info-check-icon" />
                <span>
                  <strong>Account Inquiries:</strong> Use your registered GRGPT email address so we can locate your profile.
                </span>
              </li>
              <li>
                <ShieldCheck size={16} className="info-check-icon" />
                <span>
                  <strong>Billing & Credits:</strong> Mention your plan name or transaction details for priority resolution.
                </span>
              </li>
              <li>
                <ShieldCheck size={16} className="info-check-icon" />
                <span>
                  <strong>Technical Issues:</strong> Include the relevant government department or resolution query.
                </span>
              </li>
            </ul>
          </div>

          <div className="contact-info-card">
            <div className="info-card-header">
              <div className="info-icon-badge">
                <Clock size={20} />
              </div>
              <div>
                <h3 className="info-title">Response Hours</h3>
                <p className="info-card-desc">What to expect</p>
              </div>
            </div>

            <ul className="info-checklist">
              <li>
                <ShieldCheck size={16} className="info-check-icon" />
                <span>
                  <strong>Prompt Resolution:</strong> We usually reply within 24 hours.
                </span>
              </li>
              <li>
                <ShieldCheck size={16} className="info-check-icon" />
                <span>
                  <strong>Mon &ndash; Sat:</strong> Active support coverage across regular working hours.
                </span>
              </li>
              <li>
                <ShieldCheck size={16} className="info-check-icon" />
                <span>
                  <strong>Direct Replies:</strong> Support responses will arrive in your provided inbox.
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Side: Form or Success View */}
        <div className="contact-form-card">
          {isSubmitted ? (
            <div className="contact-success-card">
              <div className="success-icon-badge">
                <CheckCircle2 size={40} />
              </div>
              <h2>Message Sent!</h2>
              <p>
                Thank you for contacting us. Your message has been routed to{" "}
                <strong>{SUPPORT_EMAIL}</strong>. Our team will review your inquiry
                and email you back at <strong>{formData.email}</strong> shortly.
              </p>

              <div className="sent-summary-box">
                <div className="summary-row">
                  <span className="summary-label">From:</span>
                  <span className="summary-val">
                    {formData.name} &lt;{formData.email}&gt;
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Subject:</span>
                  <span className="summary-val">{formData.subject}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Message:</span>
                  <span className="summary-val">{formData.message}</span>
                </div>
              </div>

              <div className="success-actions">
                <button
                  type="button"
                  className="contact-submit-btn"
                  onClick={handleResetForm}
                >
                  Send Another Message
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate("/")}
                >
                  Back to Chat
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="form-title-wrap">
                <h2>Send Us a Message</h2>
                <p>Fill out the fields below and we'll dispatch your email immediately.</p>
              </div>

              {serverError && (
                <div className="contact-alert-banner error" style={{ marginBottom: "20px" }}>
                  <AlertCircle size={18} style={{ flexShrink: 0, marginTop: "2px" }} />
                  <div>
                    <span>{serverError} </span>
                    <a href={`mailto:${SUPPORT_EMAIL}`}>Email us directly</a>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                {/* Name Field */}
                <div className="contact-form-group">
                  <label htmlFor="contact-name" className="contact-label">
                    <span>
                      Your Name <span className="required-star">*</span>
                    </span>
                  </label>
                  <div className="input-with-icon">
                    <User size={18} className="input-icon" />
                    <input
                      id="contact-name"
                      type="text"
                      className={`contact-input ${errors.name ? "input-invalid" : ""}`}
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      maxLength={120}
                      disabled={loading}
                    />
                  </div>
                  {errors.name && <span className="field-error-text">{errors.name}</span>}
                </div>

                {/* Email Field */}
                <div className="contact-form-group">
                  <label htmlFor="contact-email" className="contact-label">
                    <span>
                      Your Email Address <span className="required-star">*</span>
                    </span>
                  </label>
                  <div className="input-with-icon">
                    <Mail size={18} className="input-icon" />
                    <input
                      id="contact-email"
                      type="email"
                      className={`contact-input ${errors.email ? "input-invalid" : ""}`}
                      placeholder="e.g. rahul@example.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      maxLength={255}
                      disabled={loading}
                    />
                  </div>
                  {errors.email && <span className="field-error-text">{errors.email}</span>}
                </div>

                {/* Subject Field & Topic Chips */}
                <div className="contact-form-group">
                  <label htmlFor="contact-subject" className="contact-label">
                    <span>
                      Subject <span className="required-star">*</span>
                    </span>
                  </label>
                  <div className="input-with-icon">
                    <Tag size={18} className="input-icon" />
                    <input
                      id="contact-subject"
                      type="text"
                      className={`contact-input ${errors.subject ? "input-invalid" : ""}`}
                      placeholder="e.g. Question about daily credit renewal"
                      value={formData.subject}
                      onChange={(e) => handleInputChange("subject", e.target.value)}
                      maxLength={250}
                      disabled={loading}
                    />
                  </div>

                  {/* Quick topic chips */}
                  <div className="subject-chips-row">
                    {QUICK_TOPICS.map((topic) => (
                      <button
                        key={topic}
                        type="button"
                        className={`subject-chip ${formData.subject === topic ? "active" : ""}`}
                        onClick={() => handleSelectTopic(topic)}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>

                  {errors.subject && <span className="field-error-text">{errors.subject}</span>}
                </div>

                {/* Message Field */}
                <div className="contact-form-group">
                  <label htmlFor="contact-message" className="contact-label">
                    <span>
                      Message Details <span className="required-star">*</span>
                    </span>
                  </label>
                  <textarea
                    id="contact-message"
                    className={`contact-textarea ${errors.message ? "input-invalid" : ""}`}
                    placeholder="Describe your inquiry, issue, or question in detail so we can best assist you..."
                    value={formData.message}
                    onChange={(e) => handleInputChange("message", e.target.value)}
                    maxLength={5000}
                    disabled={loading}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {errors.message ? (
                      <span className="field-error-text">{errors.message}</span>
                    ) : (
                      <span />
                    )}
                    <span className="char-count">{formData.message.length} / 5000</span>
                  </div>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="contact-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="btn-spinner" />
                      <span>Sending Email...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span>Send to {SUPPORT_EMAIL}</span>
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
